import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, LockKey, FolderLock, ShareNetwork, SealCheck, Bell, ArrowRight, Check,
  Fingerprint, UsersThree, Buildings, User, Key, Password, DeviceMobile, ListChecks,
  QrCode, ClockCountdown, FileLock, CaretRight, Globe,
  Scan, FlowArrow, PenNib, Stack, Copy,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import Waitlist from "@/components/Waitlist";
import { fmtMoney, FLAGS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PyramidPricing from "@/components/PyramidPricing";
import BillingToggle from "@/components/BillingToggle";
import { maxAnnualSavingsPct, planHasYearly } from "@/lib/planPresentation";
import DocumentFlow from "@/components/DocumentFlow";
import SmartPlanFinder from "@/components/SmartPlanFinder";
import CompareFeatures from "@/components/CompareFeatures";
import { useAuth } from "@/context/AuthContext";

function FooterCountry() {
  const [list, setList] = useState([]);
  const [val, setVal] = useState(() => localStorage.getItem("dv_country") || "");
  useEffect(() => { api.get("/countries").then((r) => setList(r.data)).catch(() => {}); }, []);
  const onChange = (e) => { setVal(e.target.value); localStorage.setItem("dv_country", e.target.value); };
  const safeVal = list.some((c) => c.code === val) ? val : "";
  return (
    <div className="flex items-center justify-center gap-2 pb-4" data-testid="footer-country">
      <span className="text-white/30 text-xs">Region</span>
      <select value={safeVal} onChange={onChange} aria-label="Select your country or region" data-testid="footer-country-select"
        className="bg-transparent border border-white/15 rounded-lg text-white/60 text-xs px-2 py-1 focus:outline-none focus:border-primary/50">
        <option value="" className="bg-slate-900">Select country</option>
        {list.map((c) => <option key={c.code} value={c.code} label={`${c.name} — ${c.status_label}`} className="bg-slate-900" />)}
      </select>
    </div>
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1760902419069-466f6f82c8b2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.07 } }),
};

const Reveal = ({ children, i = 0, className = "", ...rest }) => (
  <motion.div {...rest} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i} variants={fade} className={className}>
    {children}
  </motion.div>
);

const Overline = ({ children }) => <p className="subhead text-xs" data-testid="section-overline">{children}</p>;

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(() => localStorage.getItem("dv_billing_interval") === "yearly");
  const [country, setCountry] = useState("US");
  const [countries, setCountries] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Region directory + best-effort default (stored → locale → IP geo → US)
  useEffect(() => {
    api.get("/countries").then(({ data }) => {
      setCountries(data);
      const codes = data.map((c) => c.code);
      const stored = localStorage.getItem("dv_pricing_country");
      const locale = (navigator.language || "").split("-")[1]?.toUpperCase();
      const initial = [stored, locale].find((x) => x && codes.includes(x));
      if (initial) setCountry(initial);
      if (!stored) api.get("/auth/geo").then(({ data: g }) => {
        if (g.supported && codes.includes(g.ip_country)) setCountry(g.ip_country);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setPlans([]); // clear stale pricing the instant the region changes
    api.get("/plans", { params: { country } }).then(({ data }) => setPlans(data)).catch(() => {});
  }, [country]);

  const selectCountry = (code) => { setCountry(code); localStorage.setItem("dv_pricing_country", code); };
  const setBilling = (yearly) => { setAnnual(yearly); localStorage.setItem("dv_billing_interval", yearly ? "yearly" : "monthly"); };

  const goRegister = () => navigate("/register");
  // Carry the chosen plan + billing into signup, then continue to Stripe Checkout.
  const startPlan = (p) => {
    if (!p || p.plan_id === "free") { goRegister(); return; }
    const q = `plan=${p.plan_id}&billing=${annual ? "yearly" : "monthly"}`;
    navigate(user ? `/dashboard/plans?${q}` : `/register?${q}`);
  };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const regionPurchasable = plans.some((p) => p.purchasable);
  const selectedName = countries.find((c) => c.code === country)?.name || "this region";

  const securityPillars = [
    { icon: LockKey, t: "Secure storage", d: "Files travel over encrypted TLS connections and are stored with our managed cloud storage provider, reachable only through short-lived authenticated links — never permanent public URLs." },
    { icon: Fingerprint, t: "Layered account security", d: "Email + password, Google sign-in, email OTP, authenticator apps and passkeys. Choose what fits you." },
    { icon: ShieldCheck, t: "Strict access control", d: "Per-user isolation means one account can never reach another's documents." },
    { icon: ListChecks, t: "Activity audit logs", d: "Every meaningful action on your account is recorded so nothing happens silently." },
  ];

  const steps = [
    { n: "01", t: "Create your vault", d: "Sign up with email or Google and switch on two-factor security in seconds." },
    { n: "02", t: "Add your documents", d: "Upload passports, certificates, policies and contracts into secure storage." },
    { n: "03", t: "Organize effortlessly", d: "Sort into categories and folders, tag, and let smart analysis suggest the details." },
    { n: "04", t: "Share & verify", d: "Send one document with a password and expiry, or issue a DuruVerify record." },
  ];

  const why = [
    { icon: FileLock, t: "Built for what matters", d: "Not another cloud drive — a focused vault for the handful of documents you cannot afford to lose." },
    { icon: SealCheck, t: "Verifiable by design", d: "DuruVerify lets a recipient confirm a document is genuine to your vault, with an honest trust model." },
    { icon: Bell, t: "Never miss a renewal", d: "Automatic reminders before a passport, licence or policy expires." },
    { icon: Globe, t: "Made for your market", d: "Pricing and support for both USD and NGN, with more markets on the roadmap." },
  ];

  const duruProducts = [
    { icon: Scan, t: "DuruScan", d: "Scan, capture and digitize important documents straight into your secure vault." },
    { icon: PenNib, t: "DuruSign", d: "Send documents for secure electronic signatures and track signing progress." },
    { icon: SealCheck, t: "DuruVerify", d: "Issue verifiable records so a recipient can confirm a document is genuine to your vault." },
    { icon: Copy, t: "DuruTwin", d: "A living digital twin of a document that tracks its status, details, timeline and next actions." },
    { icon: Stack, t: "DuruRooms", d: "Secure collaborative spaces to organize and manage documents with your team." },
    { icon: ShareNetwork, t: "Secure Sharing", d: "Share documents with passwords, expiry and revocable access — you stay in control." },
    { icon: ClockCountdown, t: "Expiration Monitoring", d: "Track expiry dates and get reminders before important documents lapse." },
    { icon: FolderLock, t: "Document Storage", d: "Store and organize personal, family and business documents securely in one vault." },
    { icon: FlowArrow, t: "DuruFlow", d: "Smart document workflows for review, approval and next actions.", soon: true },
  ];

  const vaults = [
    { id: "personal", icon: User, tag: "Personal Vault", t: "Your life's paperwork, calm and in order", d: "Keep identity documents, medical records, financial statements and warranties in one private, searchable place — reachable the moment you need them.", points: ["Passports, IDs & licences", "Insurance & financial records", "Smart categories, tags & search"] },
    { id: "family", icon: UsersThree, tag: "Family Vault", t: "Everything the household relies on", d: "Keep the documents your family depends on organized in one secure vault, so the right record is never buried on someone's laptop or lost in a drawer. Multi-user family accounts are on the way.", points: ["Organize your household's key documents", "Children's records & certificates", "Multi-user family sharing — coming soon"], soon: true },
    { id: "business", icon: Buildings, tag: "Business Vault", t: "Contracts and compliance, under control", d: "Store contracts, registrations, licences and compliance paperwork with the security and audit trail a business needs. Multi-user team accounts are coming soon.", points: ["Contracts, registrations & licences", "Expiry reminders for licences", "Team access & controlled sharing — coming soon"], soon: true },
  ];

  return (
    <div className="vault-dark min-h-screen relative overflow-x-hidden">
      {/* Nav */}
      <header className="glass sticky top-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo dark />
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <button onClick={() => scrollTo("how")} className="hover:text-white transition-colors" data-testid="nav-how">How it works</button>
            <button onClick={() => scrollTo("vaults")} className="hover:text-white transition-colors" data-testid="nav-vaults">Vaults</button>
            <button onClick={() => scrollTo("duruverify")} className="hover:text-white transition-colors" data-testid="nav-duruverify">DuruVerify</button>
            <button onClick={() => scrollTo("pricing")} className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</button>
            <button onClick={() => scrollTo("security-center")} className="hover:text-white transition-colors" data-testid="nav-security">Security</button>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-primary text-[#2a2d31] hover:bg-primary/90 font-semibold" data-testid="nav-dashboard-btn">Go to Vault</Button>
            ) : (
              <>
                <Link to="/login" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="nav-login">Login</Link>
                <Button onClick={goRegister} data-testid="nav-signup-btn" className="rounded-full bg-primary text-[#2a2d31] hover:bg-primary/90 font-semibold">Create Free Vault</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* 1. Hero */}
      <section id="main-content" tabIndex={-1} className="relative grain overflow-hidden outline-none">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="" aria-hidden width="1600" height="900" fetchPriority="high" decoding="async" className="w-full h-full object-cover opacity-40" draggable="false" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(100deg,#3a3f44 40%,rgba(58,63,68,0.72) 70%,rgba(58,63,68,0.55))" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-28 lg:pt-32 lg:pb-36">
          <div className="max-w-3xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs text-primary/90">
                <ShieldCheck size={15} weight="fill" /> Store · Protect · Organize · Share · Verify
              </span>
            </Reveal>
            <Reveal i={1}>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] mt-6">
                Your Important Documents.<br /><span className="text-primary">One Secure Vault.</span>
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-7 text-lg text-white/70 max-w-xl leading-relaxed">
                Store, organize, protect and securely share your most important personal, family and business documents — all in one place.
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="font-script text-3xl sm:text-4xl text-white/70 mt-5">Keep it safe. Keep it yours.</p>
            </Reveal>
            <Reveal i={4}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Button size="lg" onClick={goRegister} className="rounded-full h-12 px-8 text-base bg-primary text-[#2a2d31] hover:bg-primary/90 font-semibold" data-testid="hero-signup-btn">
                  Create Your Free Vault <ArrowRight size={18} className="ml-1" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => scrollTo("how")} className="rounded-full h-12 px-8 text-base bg-transparent border-primary/40 text-primary hover:bg-primary/10" data-testid="hero-how-btn">
                  See How It Works
                </Button>
              </div>
            </Reveal>
            <Reveal i={5}>
              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/45">
                <span className="flex items-center gap-1.5"><LockKey size={15} /> Encrypted in transit (TLS)</span>
                <span className="flex items-center gap-1.5"><Fingerprint size={15} /> 2FA, passkeys & session control</span>
                <span className="flex items-center gap-1.5"><Globe size={15} /> USD & NGN supported</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 1b. Document journey visualization */}
      <DocumentFlow />

      {/* 2. Trust / security overview */}
      <section id="security" className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal><Overline>Trust &amp; security</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 max-w-2xl">Designed so you'd trust it with your passport</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {securityPillars.map((p, i) => (
              <Reveal key={p.t} i={i} className="rounded-2xl border border-primary/15 bg-[#454a50] p-6 hover:-translate-y-1 transition-transform duration-300">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/15 text-primary mb-4"><p.icon size={22} weight="duotone" /></span>
                <h3 className="text-lg font-semibold">{p.t}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><Overline>How DuruVault works</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">From scattered paperwork to one calm vault</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {steps.map((s, i) => (
            <Reveal key={s.n} i={i} className="rounded-2xl border border-white/10 p-7 relative">
              <span className="font-display text-primary text-4xl font-bold">{s.n}</span>
              <h3 className="text-lg font-semibold mt-4">{s.t}</h3>
              <p className="mt-2 text-white/55 text-sm leading-relaxed">{s.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 4. Why DuruVault */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal><Overline>Why DuruVault</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 max-w-2xl">A vault, not another cloud drive</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {why.map((w, i) => (
              <Reveal key={w.t} i={i} className="rounded-2xl border border-primary/15 bg-[#454a50] p-6 hover:-translate-y-1 transition-transform duration-300">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/15 text-primary mb-4"><w.icon size={22} weight="duotone" /></span>
                <h3 className="text-lg font-semibold">{w.t}</h3>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Duru ecosystem products */}
      <section id="ecosystem" className="max-w-7xl mx-auto px-6 py-24">
        <Reveal><Overline>The DuruVault ecosystem</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 max-w-2xl">More than storage — everything your documents need</h2>
          <p className="mt-3 text-white/60 max-w-2xl">One connected ecosystem: each product works on the same secure document, so capture, signing, verification and intelligence stay in sync.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {duruProducts.map((p, i) => (
            <Reveal key={p.t} i={i} className="group rounded-2xl border border-primary/15 bg-[#454a50] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_24px_rgba(212,175,55,0.10)]" data-testid={`ecosystem-card-${p.t.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/15 text-primary mb-4 transition-transform duration-200 group-hover:scale-105"><p.icon size={22} weight="duotone" /></span>
              <h3 className="text-lg font-semibold flex items-center gap-2 flex-wrap">{p.t}{p.soon && <span className="text-[10px] uppercase tracking-wider text-primary/80 bg-primary/10 rounded-full px-2 py-0.5">Coming soon</span>}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 5-7. Vaults */}
      <section id="vaults" className="max-w-7xl mx-auto px-6 py-24 space-y-16">
        <Reveal><Overline>One platform, three ways to use it</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Personal, Family &amp; Business vaults</h2>
        </Reveal>
        {vaults.map((v, idx) => (
          <Reveal key={v.id} id={v.id}>
            <div className={`grid lg:grid-cols-2 gap-10 items-center ${idx % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <span className="inline-flex items-center gap-2 text-primary text-sm font-semibold"><v.icon size={20} weight="duotone" /> {v.tag}
                  {v.soon && <span className="ml-1 text-[10px] uppercase tracking-wider text-white/50 border border-white/20 rounded-full px-2 py-0.5 font-medium">Multi-user coming soon</span>}
                </span>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-3">{v.t}</h3>
                <p className="mt-4 text-white/60 leading-relaxed max-w-lg">{v.d}</p>
                <ul className="mt-6 space-y-3">
                  {v.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-white/80 text-sm"><Check size={18} className="text-primary mt-0.5 shrink-0" weight="bold" />{p}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-primary/15 bg-[#454a50] p-10 grid place-items-center min-h-[240px]">
                <v.icon size={96} weight="thin" className="text-primary/70" />
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* 8. Secure sharing + 9. Reminders */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-8">
          <Reveal className="rounded-3xl border border-primary/15 bg-[#454a50] p-9">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/15 text-primary mb-5"><ShareNetwork size={24} weight="duotone" /></span>
            <h3 className="font-display text-2xl font-bold tracking-tight">Secure document sharing</h3>
            <p className="mt-3 text-white/60 leading-relaxed">Share a single document — never your whole vault. Every link can be view-only or downloadable, password-protected, set to expire, and revoked at any time.</p>
            <ul className="mt-5 space-y-2.5 text-sm text-white/80">
              {["Password-protected links", "Automatic expiry dates", "Revoke access instantly", "QR codes for in-person checks"].map((s) => (
                <li key={s} className="flex items-center gap-2.5"><Key size={16} className="text-primary" weight="duotone" />{s}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal i={1} className="rounded-3xl border border-primary/15 bg-[#454a50] p-9">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/15 text-primary mb-5"><ClockCountdown size={24} weight="duotone" /></span>
            <h3 className="font-display text-2xl font-bold tracking-tight">Document expiration reminders</h3>
            <p className="mt-3 text-white/60 leading-relaxed">DuruVault watches the dates that matter and emails you before a passport, licence, policy or contract lapses — so a renewal never sneaks up on you.</p>
            <ul className="mt-5 space-y-2.5 text-sm text-white/80">
              {["Choose 7, 30 or 90-day reminders", "Email alerts to your inbox", "Set it once when you upload", "Optional AI-detected expiry dates"].map((s) => (
                <li key={s} className="flex items-center gap-2.5"><Bell size={16} className="text-primary" weight="duotone" />{s}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* 10. DuruVerify */}
      <section id="duruverify" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>DuruVerify</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Prove a document is genuine — honestly</h2>
            <p className="mt-4 text-white/60 leading-relaxed max-w-lg">Issue a secure verification link or QR code for any document. Recipients confirm it against a DuruVault-issued record and its integrity checksum — while the private file stays private.</p>
            <div className="mt-7 space-y-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0"><SealCheck size={18} weight="fill" /></span>
                <div><p className="font-semibold text-sm">Verified by DuruVault</p><p className="text-sm text-white/55">Confirms the record was created by the vault owner and the file matches its checksum.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-sky-500/15 text-sky-300 grid place-items-center shrink-0"><Buildings size={18} weight="fill" /></span>
                <div><p className="font-semibold text-sm">Verified by an issuing institution</p><p className="text-sm text-white/55">Reserved for records attested by approved institutions — our participation layer for the future.</p></div>
              </div>
            </div>
            <p className="mt-6 text-xs text-white/40 max-w-lg">We're deliberately honest: DuruVault does not claim to independently certify a government-issued document unless a verified institution has attested it.</p>
            <Link to="/for-institutions" className="mt-6 inline-flex items-center gap-1.5 text-primary hover:underline text-sm font-medium" data-testid="landing-institution-apply">Are you an institution? Apply to issue verifications <ArrowRight size={15} /></Link>
          </Reveal>
          <Reveal i={1} className="rounded-3xl border border-primary/15 bg-[#454a50] p-10 flex flex-col items-center text-center">
            <span className="w-20 h-20 rounded-full bg-primary/15 text-primary grid place-items-center mb-5"><QrCode size={40} weight="duotone" /></span>
            <p className="font-display text-2xl font-bold">Scan. Confirm. Trust.</p>
            <p className="text-white/55 text-sm mt-2 max-w-xs">Every verification page shows a unique ID, status, timestamp and trust level — nothing more than necessary.</p>
          </Reveal>
        </div>
      </section>

      <Seo path="/" description="Store, organize, protect, share and verify your most important personal, family and business documents — all in one secure vault." />
      {/* 11. Pricing */}
      <section id="pricing" className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex flex-col items-center text-center mb-12">
            <Overline>Pricing</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Plans that grow with you</h2>
            <p className="mt-3 text-white/60">From a free starter vault to business-scale storage.</p>
            <div className="mt-6 flex flex-col items-center gap-2" data-testid="landing-country-toggle">
              <span className="text-white/40 text-xs uppercase tracking-wider">Country / region</span>
              <select value={country} onChange={(e) => selectCountry(e.target.value)} aria-label="Select your country or region" data-testid="landing-country-select"
                className="bg-white/5 border border-white/15 rounded-full text-white text-sm px-5 py-2 focus:outline-none focus:border-primary/60 min-w-[280px] text-center">
                {countries.map((c) => (
                  <option key={c.code} value={c.code} label={`${FLAGS[c.code] || "🏳️"} ${c.name} · ${c.currency} · ${c.status_label}`} className="bg-slate-900" />
                ))}
              </select>
            </div>
            {plans.some(planHasYearly) && (
              <div className="mt-5 flex flex-col items-center gap-2" data-testid="landing-billing-toggle">
                <BillingToggle value={annual ? "yearly" : "monthly"} onChange={(v) => setBilling(v === "yearly")} savingsPct={maxAnnualSavingsPct(plans)} dark />
                {annual && maxAnnualSavingsPct(plans) ? (
                  <p className="text-primary text-sm font-medium" data-testid="landing-annual-savings-callout">
                    {(() => { const pct = maxAnnualSavingsPct(plans); const mf = Math.round((pct / 100) * 12); return `Pay yearly${mf > 0 ? ` — get ${mf} month${mf === 1 ? "" : "s"} free` : ""} and save up to ${pct}%`; })()}
                  </p>
                ) : null}
              </div>
            )}
          </div>
          <PyramidPricing theme="dark" plans={plans} mode="public" interval={annual ? "yearly" : "monthly"} onFree={goRegister} onSelect={startPlan} onWaitlist={() => scrollTo("waitlist")} />
          <div className="mt-12"><SmartPlanFinder theme="dark" plans={plans} onSelect={startPlan} /></div>
          <CompareFeatures theme="dark" plans={plans} />
          {plans.length > 0 && !regionPurchasable && (
            <div id="waitlist" className="mt-10 max-w-xl mx-auto rounded-2xl border border-primary/30 bg-primary/[0.06] p-6 text-center" data-testid="landing-waitlist">
              <h3 className="font-display text-xl font-semibold">Paid plans are coming to {selectedName}</h3>
              <p className="text-white/55 text-sm mt-2 mb-4">Checkout isn't live in this region yet. Leave your email and we'll notify you the moment paid plans launch — the free vault is available today.</p>
              <Waitlist country={country} countryName={selectedName} dark />
            </div>
          )}
          <p className="text-center text-xs text-white/40 mt-8" data-testid="landing-pricing-note">
            {!regionPurchasable
              ? "The free vault is available today. Paid plans and local pricing launch region by region — displayed availability doesn't imply DuruVault is fully launched everywhere shown."
              : plans.some((p) => p.purchasable)
              ? `Billed ${annual ? "annually" : "monthly"} via Stripe. Prices shown in ${plans[0]?.currency || "USD"}.`
              : plans.some((p) => p.local_pricing)
              ? `Prices shown in ${plans[0]?.currency || "USD"} for your region. Checkout is expanding here soon.`
              : "Prices shown in USD."}
          </p>
        </div>
      </section>

      {/* 12. FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <Reveal className="text-center mb-10">
          <Overline>FAQ</Overline>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Questions, answered</h2>
        </Reveal>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "Is DuruVault just another cloud drive?", a: "No. It's a focused vault for important documents, with strong security, expiration reminders, safe single-document sharing and DuruVerify built in." },
            { q: "Can anyone at DuruVault read my documents?", a: "Administrators can see usage statistics for running the service, but do not get automatic access to your private document contents." },
            { q: "How does sharing keep my vault private?", a: "You share one document at a time via a link that can be view-only or downloadable, password protected, expiring and revocable — never your whole vault." },
            { q: "What is DuruVerify?", a: "A way to issue a verification record for a document. A recipient can confirm it exists in your vault and matches its checksum, without seeing the file. We clearly distinguish DuruVault self-attested records from institution-verified ones." },
            { q: "Do you support Nigeria and the US?", a: "Yes. Pricing and billing support both USD and NGN today, with more markets planned." },
            { q: "Do you use my national ID as a password?", a: "Never. Government ID numbers are never used as credentials." },
          ].map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="border border-white/10 rounded-xl px-5 bg-white/[0.02]">
              <AccordionTrigger className="text-left text-white hover:no-underline" data-testid={`faq-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-white/60">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* 13. Security Center */}
      <section id="security-center" className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <Overline>Security Center</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Our security posture, in plain language</h2>
            <p className="mt-5 text-white/60 leading-relaxed">We believe trust is earned by being specific and honest — not by plastering unearned badges. Here is how we protect your documents today.</p>
            <p className="mt-6 text-xs text-white/40 leading-relaxed">We don't claim to be "100% secure" — no system is. We apply layered, industry-standard protections and keep improving them. We do not display certifications we have not earned.</p>
          </Reveal>
          <Reveal i={1}>
            <ul className="space-y-4">
              {[
                { icon: LockKey, t: "Encryption in transit (TLS)", d: "Documents travel over encrypted TLS connections and are served only via short-lived authenticated links, not public URLs." },
                { icon: Password, t: "Multiple authentication options", d: "Password, Google, email OTP, authenticator apps and passkeys." },
                { icon: DeviceMobile, t: "Session & device control", d: "See active sessions and sign out any device at any time." },
                { icon: FolderLock, t: "Per-user isolation", d: "Strict authorization prevents any cross-account access." },
                { icon: ListChecks, t: "Audit logging", d: "Account activity is recorded, including verification checks." },
                { icon: ShareNetwork, t: "Controlled sharing", d: "Password, expiry and revocation on every shared link." },
              ].map((s) => (
                <li key={s.t} className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#454a50] p-4">
                  <span className="w-10 h-10 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0"><s.icon size={20} weight="duotone" /></span>
                  <div><p className="font-semibold text-sm">{s.t}</p><p className="text-sm text-white/55 mt-0.5">{s.d}</p></div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* 14. Final CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal className="rounded-3xl border border-primary/25 bg-primary/10 p-12 lg:p-16 text-center relative overflow-hidden grain">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">Your documents deserve a real vault</h2>
          <p className="mt-4 text-white/65 max-w-xl mx-auto">Start free today and keep your most important paperwork safe, organized and verifiable.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Button size="lg" onClick={goRegister} className="rounded-full h-12 px-8 bg-primary text-[#2a2d31] hover:bg-primary/90 font-semibold" data-testid="cta-signup-btn">Create Your Free Vault <ArrowRight size={18} className="ml-1" /></Button>
            <Button size="lg" variant="outline" onClick={() => scrollTo("how")} className="rounded-full h-12 px-8 bg-transparent border-primary/40 text-primary hover:bg-primary/10" data-testid="cta-how-btn">See How It Works</Button>
          </div>
        </Reveal>
      </section>

      {/* 15. Footer */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left" data-testid="global-availability-band">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center shrink-0"><Globe size={24} className="text-primary" weight="fill" /></div>
            <div>
              <p className="font-display text-xl font-semibold">Built for a connected world</p>
              <p className="text-white/50 text-sm mt-1">Secure storage, sharing and verification across borders — availability may vary by jurisdiction.</p>
            </div>
          </div>
          <Link to="/countries" data-testid="global-availability-cta" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 hover:border-primary hover:text-primary transition-colors whitespace-nowrap">
            Explore availability by country <CaretRight size={15} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src="/logo-full-white.png" alt="DuruVault" className="h-28 w-auto" draggable="false" />
            <p className="text-white/45 text-sm mt-4 max-w-sm">A secure digital vault for the documents that matter most — store, organize, protect, share and verify.</p>
          </div>
          <div>
            <p className="subhead text-xs mb-4">Product</p>
            <ul className="space-y-2.5 text-sm text-white/55">
              <li><button onClick={() => scrollTo("how")} className="hover:text-white transition-colors">How it works</button></li>
              <li><button onClick={() => scrollTo("vaults")} className="hover:text-white transition-colors">Vaults</button></li>
              <li><button onClick={() => scrollTo("duruverify")} className="hover:text-white transition-colors">DuruVerify</button></li>
              <li><Link to="/for-institutions" className="hover:text-white transition-colors">For institutions</Link></li>
              <li><button onClick={() => scrollTo("pricing")} className="hover:text-white transition-colors">Pricing</button></li>
              <li><Link to="/countries" className="hover:text-white transition-colors" data-testid="footer-countries-link">Global Availability</Link></li>
            </ul>
          </div>
          <div>
            <p className="subhead text-xs mb-4">Legal</p>
            <ul className="space-y-2.5 text-sm text-white/55">
              <li><Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link></li>
              <li><Link to="/legal/ai" className="hover:text-white transition-colors">AI Terms</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-white transition-colors">Cookies</Link></li>
              <li><Link to="/legal/security" className="hover:text-white transition-colors">Security</Link></li>
              <li><a href="mailto:support@duruvault.com" className="hover:text-white transition-colors" data-testid="footer-support">Contact Support</a></li>
              <li><Link to="/legal/faq" className="hover:text-white transition-colors" data-testid="footer-help">Help Center</Link></li>
              <li><a href="https://status.duruvault.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-status">System Status</a></li>
              <li><Link to="/legal/security" className="hover:text-white transition-colors" data-testid="footer-trust">Trust &amp; Security</Link></li>
              <li><a href="mailto:security@duruvault.com" className="hover:text-white transition-colors" data-testid="footer-report-security">Report a Security Issue</a></li>
              <li><Link to="/legal/data-deletion" className="hover:text-white transition-colors">Data deletion</Link></li>
              <li><Link to="/legal/dpa" className="hover:text-white transition-colors" data-testid="footer-legal-dpa">DPA</Link></li>
              <li><Link to="/legal/subprocessors" className="hover:text-white transition-colors" data-testid="footer-legal-subprocessors">Subprocessors</Link></li>
              <li><Link to="/legal/esign" className="hover:text-white transition-colors" data-testid="footer-legal-esign">E-Signature Terms</Link></li>
              <li><Link to="/legal/countries" className="hover:text-white transition-colors" data-testid="footer-legal-countries">Jurisdictions</Link></li>
            </ul>
          </div>
        </div>
        <FooterCountry />
        <p className="text-center text-white/30 text-xs pb-10">© {new Date().getFullYear()} DuruVault. All rights reserved.</p>
      </footer>
    </div>
  );
}
